/**
 * Print view for a single MyApp entry.
 *
 * Opens in a new tab from the Print button on submit.tsx and
 * HeaderLineForm.tsx. Fetches the entry (simple or composite), the
 * table metadata (and child-table metadata for composites), and the
 * per-record attachments list, then renders a clean read-only document
 * via React Portal mounted directly on document.body. The portal
 * mount escapes the portal `<AppLayout>` tree entirely, so no ancestor
 * styling (sidebar bg, header bg-primary, body padding) can leak into
 * the print output. A scoped `@media print` rule hides everything else
 * on the page so window.print() only captures the document.
 *
 * Auto-fires `window.print()` once data + fonts are ready.
 *
 * Search params:
 *   id      required record id (number)
 *   mode    "preview" to skip the auto-print call (dev convenience)
 */

import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "@/contexts/AuthContext";
import { myappApi } from "@/services/api/myapp";
import type { FormDetail } from "@/types/myapp";
import {
  PrintableEntryView,
  type AttachmentRow,
  type LineSection,
} from "@/components/portal/tables/PrintableEntryView";

type PrintSearch = {
  id?: number;
  mode?: "preview";
};

function validatePrintSearch(search: Record<string, unknown>): PrintSearch {
  const raw = search.id;
  let id: number | undefined;
  if (typeof raw === "number" && !Number.isNaN(raw)) id = raw;
  else if (typeof raw === "string" && raw.trim() !== "") {
    const n = Number(raw);
    if (!Number.isNaN(n)) id = n;
  }
  const mode = search.mode === "preview" ? "preview" : undefined;
  return { id, mode };
}

export const Route = createFileRoute("/portal/myapp/$tableId/print")({
  component: PrintEntryPage,
  validateSearch: validatePrintSearch,
});

type LoadedData = {
  headerTable: FormDetail;
  headerRow: Record<string, unknown>;
  headerAttachments: AttachmentRow[];
  lineSections: LineSection[];
  diagnostics: PrintDiagnostics;
};

export type PrintDiagnostics = {
  tableId: string;
  tableName: string;
  isHeaderTable: boolean;
  isCompositeBranch: boolean;
  childTablesFromApi: Array<{ id: string; name: string }>;
  linesByChildTableKeys: string[] | null;
  rowsPerChild: Record<string, { childId: string; childName: string; rowCount: number }>;
};

function usePortalMount(): HTMLDivElement | null {
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = document.createElement("div");
    el.setAttribute("data-lp-root", "");
    document.body.appendChild(el);
    setNode(el);
    return () => {
      el.parentNode?.removeChild(el);
    };
  }, []);
  return node;
}

/**
 * Inject the print stylesheet directly into `document.head` rather than
 * as a React child. Rendering `<style>` inside the component tree was
 * fragile: the AppLayout `<main>` wrapper and React 19's style hoisting
 * combined to produce a sheet that wasn't reliably picked up by the
 * print render. A `<style>` element in `document.head` is unambiguous
 * and applies globally regardless of where the React tree mounts.
 */
function usePrintStyles(css: string): void {
  useEffect(() => {
    const el = document.createElement("style");
    el.setAttribute("data-lp-styles", "");
    el.textContent = css;
    document.head.appendChild(el);
    return () => {
      el.parentNode?.removeChild(el);
    };
  }, [css]);
}

function PrintEntryPage() {
  const { tableId } = Route.useParams();
  const { id: recordId, mode } = Route.useSearch();
  const { token, user } = useAuth();

  const [data, setData] = useState<LoadedData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const printedAtIso = useMemo(() => new Date().toISOString(), []);
  const portalNode = usePortalMount();
  usePrintStyles(PRINT_STYLES);
  const hasPrintedRef = useRef(false);

  // Toggle body class so the print stylesheet can scope @media print rules
  // to this page only (and not pollute any other portal route that happens
  // to be mounted concurrently).
  useEffect(() => {
    document.body.classList.add("lp-print-mode");
    return () => {
      document.body.classList.remove("lp-print-mode");
    };
  }, []);

  useEffect(() => {
    if (!tableId || recordId == null) {
      setError("Missing table id or record id");
      return;
    }
    let cancelled = false;
    async function load(): Promise<void> {
      try {
        const headerTable = await myappApi.tables.get(token, tableId);
        const isComposite = headerTable.is_header_table === true;

        const recordPromise = isComposite
          ? myappApi.composite.get(token, tableId, recordId as number)
          : myappApi.records.get(token, tableId, recordId as number);

        const childrenPromise = isComposite
          ? myappApi.tables
              .getChildren(token, tableId)
              .then((children) =>
                Promise.all(
                  children.map((c) => myappApi.tables.get(token, c.id)),
                ),
              )
          : Promise.resolve([] as FormDetail[]);

        const headerAttachmentsPromise = myappApi.attachments.listAttachments(
          token,
          tableId,
          recordId as number,
        );

        const [recordResult, childTables, headerAttachments] = await Promise.all(
          [recordPromise, childrenPromise, headerAttachmentsPromise],
        );

        let headerRow: Record<string, unknown>;
        let lineSections: LineSection[] = [];
        let linesByChildTableKeys: string[] | null = null;
        const rowsPerChild: PrintDiagnostics["rowsPerChild"] = {};

        if (isComposite) {
          const composite = recordResult as {
            header: Record<string, unknown>;
            lines_by_child_table: Record<string, Record<string, unknown>[]>;
          };
          headerRow = composite.header;
          linesByChildTableKeys = Object.keys(
            composite.lines_by_child_table ?? {},
          );

          lineSections = await Promise.all(
            childTables.map(async (child) => {
              const rows = composite.lines_by_child_table?.[child.id] ?? [];
              rowsPerChild[child.id] = {
                childId: child.id,
                childName: child.name,
                rowCount: rows.length,
              };
              const pkCol = child.primary_key_column;
              const attachmentLists = await Promise.all(
                rows.map((row) => {
                  const pk = Number(row[pkCol]);
                  if (!Number.isFinite(pk)) {
                    return Promise.resolve<AttachmentRow[]>([]);
                  }
                  return myappApi.attachments.listAttachments(
                    token,
                    child.id,
                    pk,
                  );
                }),
              );
              const attachmentsByRowPk: Record<number, AttachmentRow[]> = {};
              rows.forEach((row, idx) => {
                const pk = Number(row[pkCol]);
                if (Number.isFinite(pk)) {
                  attachmentsByRowPk[pk] = attachmentLists[idx] ?? [];
                }
              });
              return { childTable: child, rows, attachmentsByRowPk };
            }),
          );
        } else {
          headerRow = recordResult as Record<string, unknown>;
        }

        const diagnostics: PrintDiagnostics = {
          tableId,
          tableName: headerTable.name,
          isHeaderTable: headerTable.is_header_table === true,
          isCompositeBranch: isComposite,
          childTablesFromApi: childTables.map((c) => ({
            id: c.id,
            name: c.name,
          })),
          linesByChildTableKeys,
          rowsPerChild,
        };

        console.debug("[print] diagnostics", diagnostics);

        if (cancelled) return;
        setData({
          headerTable,
          headerRow,
          headerAttachments,
          lineSections,
          diagnostics,
        });
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error ? err.message : "Failed to load record for print",
        );
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [tableId, recordId, token]);

  useEffect(() => {
    if (!data) return;
    if (mode === "preview") return;
    if (hasPrintedRef.current) return;
    hasPrintedRef.current = true;
    const fire = (): void => window.print();
    if (typeof document !== "undefined" && "fonts" in document) {
      void (
        document as Document & { fonts: { ready: Promise<unknown> } }
      ).fonts.ready.then(() => fire());
    } else {
      fire();
    }
  }, [data, mode]);

  if (error) {
    return (
      <div className="p-6 text-sm text-destructive">
        Failed to load print view: {error}
      </div>
    );
  }
  if (!data || !portalNode) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        Preparing print view...
      </div>
    );
  }

  const isPreview = mode === "preview";

  return (
    <>
      {createPortal(
        <div className="lp-root" data-lp-content>
          <PrintableEntryView
            headerTable={data.headerTable}
            headerRow={data.headerRow}
            headerAttachments={data.headerAttachments}
            lineSections={data.lineSections}
            printContext={{
              printedByEmail: user?.email ?? "",
              printedAtIso,
            }}
          />
        </div>,
        portalNode,
      )}
      <div className="p-6 text-sm text-muted-foreground space-y-3">
        {isPreview ? (
          <div className="rounded border border-amber-300 bg-amber-50 p-3 font-mono text-xs text-slate-900">
            <div className="mb-2 font-sans text-sm font-semibold text-amber-900">
              Print preview diagnostics ({"?mode=preview"})
            </div>
            <div>tableId: {data.diagnostics.tableId}</div>
            <div>tableName: {data.diagnostics.tableName}</div>
            <div>is_header_table: {String(data.diagnostics.isHeaderTable)}</div>
            <div>
              isCompositeBranch:{" "}
              {String(data.diagnostics.isCompositeBranch)}
            </div>
            <div>
              childTablesFromApi:{" "}
              {data.diagnostics.childTablesFromApi.length === 0
                ? "[]"
                : data.diagnostics.childTablesFromApi
                    .map((c) => `${c.name} (${c.id})`)
                    .join(", ")}
            </div>
            <div>
              lines_by_child_table keys:{" "}
              {data.diagnostics.linesByChildTableKeys === null
                ? "(simple path, no composite fetch)"
                : data.diagnostics.linesByChildTableKeys.length === 0
                ? "(empty object)"
                : data.diagnostics.linesByChildTableKeys.join(", ")}
            </div>
            <div>rowsPerChild:</div>
            <pre className="mt-1 whitespace-pre-wrap break-all">
              {JSON.stringify(data.diagnostics.rowsPerChild, null, 2)}
            </pre>
          </div>
        ) : (
          <div>
            Print dialog should have opened. If not,{" "}
            <button
              type="button"
              className="underline"
              onClick={() => window.print()}
            >
              click here to print
            </button>
            .
          </div>
        )}
      </div>
    </>
  );
}

const PRINT_STYLES = `
/* ============================================================
 * Print document styles. Injected into <head> via usePrintStyles
 * so the rules apply globally and survive the portal mount.
 * Heavily overridden with !important inside @media print to win
 * against any Tailwind base styles still in the cascade.
 * ============================================================ */

[data-lp-root] {
  all: initial;
}

[data-lp-root] .lp-root {
  display: block;
  background: white;
  color: #111827;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Inter', 'Helvetica Neue', Arial, sans-serif;
  font-size: 11pt;
  line-height: 1.45;
  max-width: 820px;
  margin: 24px auto;
  padding: 28px 32px;
  border: 1px solid #cbd5e1;
  box-sizing: border-box;
}

[data-lp-root] .lp-doc {
  background: white;
}

[data-lp-root] .lp-doc-header {
  border-bottom: 3px solid #0f172a;
  padding-bottom: 10px;
  margin-bottom: 18px;
}

[data-lp-root] .lp-doc-title {
  display: block;
  font-size: 22pt;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 4px 0;
  line-height: 1.15;
  letter-spacing: -0.01em;
}

[data-lp-root] .lp-doc-record {
  display: block;
  font-size: 10.5pt;
  color: #475569;
}

[data-lp-root] .lp-doc-description {
  display: block;
  font-size: 10pt;
  color: #475569;
  margin-top: 6px;
}

[data-lp-root] .lp-audit-strip {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 4px 24px;
  font-size: 9.5pt;
  color: #334155;
  margin-bottom: 18px;
  padding: 10px 14px;
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  page-break-inside: avoid;
}

[data-lp-root] .lp-audit-strip > div {
  line-height: 1.4;
}

[data-lp-root] .lp-audit-label {
  font-weight: 700;
  color: #0f172a;
}

[data-lp-root] .lp-section {
  margin-bottom: 22px;
  page-break-inside: avoid;
}

[data-lp-root] .lp-section-title {
  display: block;
  font-size: 14pt;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 10px 0;
  padding: 4px 8px;
  background: #e2e8f0;
  border-left: 4px solid #0f172a;
}

/* ---- Header fields: key/value table ---- */
[data-lp-root] .lp-kv-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 10.5pt;
  border: 1px solid #cbd5e1;
  table-layout: fixed;
}

[data-lp-root] .lp-kv-table tr {
  border-bottom: 1px solid #e2e8f0;
}

[data-lp-root] .lp-kv-table tr:last-child {
  border-bottom: none;
}

[data-lp-root] .lp-kv-label {
  width: 35%;
  text-align: left;
  font-weight: 600;
  color: #1e293b;
  padding: 7px 12px;
  vertical-align: top;
  background: #f8fafc;
  border-right: 1px solid #e2e8f0;
}

[data-lp-root] .lp-kv-value {
  color: #111827;
  padding: 7px 12px;
  vertical-align: top;
  word-break: break-word;
}

/* ---- Line tables (advanced/composite) ---- */
[data-lp-root] .lp-line-section {
  /* Each line table starts on a new page so the auditor sees
   * exactly one child section per page. */
  page-break-before: always;
  break-before: page;
}

[data-lp-root] .lp-line-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 9.5pt;
  margin-top: 6px;
  border: 1px solid #94a3b8;
  /* Theme 10d v3: stay on table-layout: auto (default). The browser's
     auto algorithm sizes each column proportional to its content -
     a 3-character "Reference" column stays narrow, a long "Detail"
     column gets the room it needs. v2 used table-layout: fixed
     (equal widths) which wasted space on short columns and squeezed
     long-text columns into walls of wrapped text. The actual root
     cause of the v1 "2026-05- / 25" wrap was the hyphen-as-soft-
     break behaviour, which is fixed independently by word-break:
     keep-all on the cells below. */
}

[data-lp-root] .lp-line-table th,
[data-lp-root] .lp-line-table td {
  border: 1px solid #cbd5e1;
  padding: 6px 8px;
  text-align: left;
  vertical-align: top;
  /* break-word: long values without whitespace (e.g. a 200-char
     attachment filename) still wrap rather than overflowing the cell
     and breaking the table's grid. */
  overflow-wrap: break-word;
  /* keep-all: prevents the browser treating U+002D hyphens as
     soft-break opportunities. Without this, dates like "2026-05-25"
     and IDs like "PC-000015" split mid-string under width pressure
     (e.g. "2026-05- / 25"). Whitespace remains a valid break point
     so multi-word headers like "Reason for Spending" wrap on spaces
     when the column is narrow. */
  word-break: keep-all;
}

[data-lp-root] .lp-line-table th {
  background: #cbd5e1;
  font-weight: 700;
  color: #0f172a;
  border-bottom: 2px solid #475569;
}

[data-lp-root] .lp-line-table tbody tr:nth-child(even) td {
  background: #f8fafc;
}

/* Theme 10d v4: shrink-to-fit cascade for wide tables.
   See LANDSCAPE_COLUMN_THRESHOLD / COMPACT / ULTRA_COMPACT in
   PrintableEntryView.tsx for the trigger points. Each modifier
   compounds: ultra-compact ALWAYS also has the compact + landscape
   classes, so the cascade is naturally additive. */

[data-lp-root] .lp-line-section--compact .lp-line-table {
  font-size: 8pt;
}

[data-lp-root] .lp-line-section--compact .lp-line-table th,
[data-lp-root] .lp-line-section--compact .lp-line-table td {
  padding: 4px 6px;
}

[data-lp-root] .lp-line-section--ultra-compact .lp-line-table {
  font-size: 7pt;
}

[data-lp-root] .lp-line-section--ultra-compact .lp-line-table th,
[data-lp-root] .lp-line-section--ultra-compact .lp-line-table td {
  padding: 3px 4px;
}

[data-lp-root] .lp-line-audit {
  margin-top: 8px;
  font-size: 8.5pt;
  color: #64748b;
  border-top: 1px dashed #cbd5e1;
  padding-top: 6px;
}

[data-lp-root] .lp-line-audit-row {
  padding: 1px 0;
}

/* ---- Attachments ---- */
[data-lp-root] .lp-attachment-group {
  margin-bottom: 8px;
}

[data-lp-root] .lp-attachment-field {
  font-weight: 600;
  font-size: 10pt;
  margin-bottom: 4px;
  color: #1e293b;
}

[data-lp-root] .lp-attachment-list,
[data-lp-root] .lp-attachment-inline {
  margin: 0;
  padding-left: 20px;
  color: #111827;
  list-style: disc;
}

[data-lp-root] .lp-meta {
  color: #64748b;
  font-size: 8.5pt;
}

[data-lp-root] .lp-empty {
  font-style: italic;
  color: #94a3b8;
  font-size: 10pt;
  padding: 4px 0;
}

[data-lp-root] .lp-doc-footer {
  margin-top: 24px;
  padding-top: 8px;
  border-top: 1px solid #cbd5e1;
  font-size: 9pt;
  color: #475569;
}

/* ============================================================
 * @page + @media print
 *
 * Default A4 portrait. Line tables with many columns add the
 * lp-line-section--landscape class (see LANDSCAPE_COLUMN_THRESHOLD
 * in PrintableEntryView), which switches them onto a named
 * landscape page. Chromium-based browsers (Chrome/Edge, our
 * primary targets) respect named-page orientation; Firefox is
 * patchy.
 * ============================================================ */

@page {
  size: A4 portrait;
  margin: 18mm 14mm;
  /* Theme 10c: page number footer. CSS Paged Media @page rules are
     supported in Chromium-based browsers (the target for SWA "Save
     as PDF" + production users). Firefox lacks the @bottom-* boxes
     and will fall back to the browser-native footer (or no footer).
     Acceptable for v1; documented as a known limitation. */
  @bottom-right {
    content: "Page " counter(page) " of " counter(pages);
    font-family: ui-sans-serif, system-ui, sans-serif;
    font-size: 9pt;
    color: #6b7280;
  }
}

@page lp-landscape {
  size: A4 landscape;
  margin: 14mm 18mm;
  /* Mirror the portrait footer for landscape pages so the page
     count is consistent across orientations. */
  @bottom-right {
    content: "Page " counter(page) " of " counter(pages);
    font-family: ui-sans-serif, system-ui, sans-serif;
    font-size: 9pt;
    color: #6b7280;
  }
}

[data-lp-root] .lp-line-section--landscape {
  page: lp-landscape;
}

@media print {
  html, body {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  body.lp-print-mode > *:not([data-lp-root]) {
    display: none !important;
  }
  body.lp-print-mode [data-lp-root] {
    display: block !important;
  }
  body.lp-print-mode [data-lp-root] .lp-root {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    background: white !important;
    max-width: none !important;
    margin: 0 !important;
    padding: 0 !important;
    border: none !important;
  }
}
`;
